import Close from '@mui/icons-material/Close';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { GeofenceTriggerType } from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useState } from 'react';

import { OverflowAwareText } from '~components/Order/ordersDispatchStyledComponents';
import { GeofenceTrigger } from '~hooks/useGeofenceTriggers/models';
import { Job } from '~hooks/useJob/models';
import { useJob } from '~hooks/useJob/useJob';
import { useLiveMap } from '~hooks/useLiveMap/useLiveMap';
import type { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { FilterByType } from './GeofenceConfiguration';
import GeofenceTriggerCard from './GeofenceTriggerCard';

interface GeofenceResultsPanelProps {
  filterBy: FilterByType;
  onClose: () => void;
  onViewRoute?: (pings: LocationHistoryMinimal[], job?: Job) => void;
  onViewRouteLoadingChange?: (isLoading: boolean) => void;
  triggers: GeofenceTrigger[];
}

interface GroupedTrigger {
  groupId: string;
  groupName: string;
  triggers: GeofenceTrigger[];
  enterCount: number;
  exitCount: number;
}

function groupTriggersByEquipment(triggers: GeofenceTrigger[]): GroupedTrigger[] {
  const grouped = new Map<string, GroupedTrigger>();

  triggers.forEach((trigger) => {
    const equipment = trigger.triggeredLocation.equipment;
    const groupId = equipment?.id ?? t('live_map.geofence_reports.results_panel.unknown');
    const groupName =
      equipment?.name ?? t('live_map.geofence_reports.results_panel.unknown_equipment');

    if (!grouped.has(groupId)) {
      grouped.set(groupId, {
        groupId,
        groupName,
        triggers: [],
        enterCount: 0,
        exitCount: 0,
      });
    }

    const group = grouped.get(groupId)!;
    group.triggers.push(trigger);

    if (trigger.triggerType === GeofenceTriggerType.ENTER) {
      group.enterCount++;
    } else {
      group.exitCount++;
    }
  });

  return Array.from(grouped.values());
}

function groupTriggersByDriver(triggers: GeofenceTrigger[]): GroupedTrigger[] {
  const grouped = new Map<string, GroupedTrigger>();

  triggers.forEach((trigger) => {
    const driver = trigger.triggeredLocation.driver;
    const groupId = driver?.id ?? t('live_map.geofence_reports.results_panel.unknown');
    const groupName =
      driver?.fullName ?? t('live_map.geofence_reports.results_panel.unknown_driver');

    if (!grouped.has(groupId)) {
      grouped.set(groupId, {
        groupId,
        groupName,
        triggers: [],
        enterCount: 0,
        exitCount: 0,
      });
    }

    const group = grouped.get(groupId)!;
    group.triggers.push(trigger);

    if (trigger.triggerType === GeofenceTriggerType.ENTER) {
      group.enterCount++;
    } else {
      group.exitCount++;
    }
  });

  return Array.from(grouped.values());
}

export default function GeofenceResultsPanel({
  filterBy,
  onClose,
  onViewRoute,
  onViewRouteLoadingChange,
  triggers,
}: GeofenceResultsPanelProps) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { fetchTreadRoute } = useLiveMap(companyId);
  const { getJobById } = useJob();
  const [loadingJobId, setLoadingJobId] = useState<string | null>(null);

  const handleViewRoute = async (jobId: string) => {
    setLoadingJobId(jobId);
    onViewRouteLoadingChange?.(true);
    try {
      // Fetch both route pings and job details (for waypoints/site markers) in parallel
      const [pings, job] = await Promise.all([
        fetchTreadRoute(jobId),
        getJobById(jobId, companyId),
      ]);
      onViewRoute?.(pings, job);
    } finally {
      setLoadingJobId(null);
      onViewRouteLoadingChange?.(false);
    }
  };

  const isGroupedByDriver = filterBy === FilterByType.DRIVER;
  const groupedTriggers = isGroupedByDriver
    ? groupTriggersByDriver(triggers)
    : groupTriggersByEquipment(triggers);
  const totalGroups = groupedTriggers.length;
  const totalEvents = triggers.length;
  const groupLabel = isGroupedByDriver
    ? t('live_map.geofence_reports.results_panel.drivers')
    : t('live_map.geofence_reports.results_panel.trucks');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${theme.brandV2.colors.treadGray7}`,
          backgroundColor: theme.brandV2.colors.treadGray9,
        })}
      >
        <Typography
          sx={(theme) => ({
            fontSize: '14px',
            fontWeight: 600,
            color: theme.brandV2.colors.treadBlack,
          })}
        >
          {t('live_map.geofence_reports.results_panel.results')} ({totalGroups}{' '}
          {groupLabel}, {totalEvents}{' '}
          {t('live_map.geofence_reports.results_panel.events')})
        </Typography>

        <IconButton size="small" onClick={onClose}>
          <Close
            sx={(theme) => ({ fontSize: 18, color: theme.brandV2.colors.treadGray3 })}
          />
        </IconButton>
      </Box>

      <Box sx={{ overflow: 'auto' }}>
        {groupedTriggers.map((group) => (
          <TriggerGroup
            key={group.groupId}
            group={group}
            loadingJobId={loadingJobId}
            onViewClick={handleViewRoute}
          />
        ))}
      </Box>
    </Box>
  );
}

interface TriggerGroupProps {
  group: GroupedTrigger;
  loadingJobId: string | null;
  onViewClick: (jobId: string) => void;
}

function TriggerGroup({ group, loadingJobId, onViewClick }: TriggerGroupProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const totalEvents = group.triggers.length;

  return (
    <Box
      sx={(theme) => ({ borderBottom: `1px solid ${theme.brandV2.colors.treadGray7}` })}
    >
      <Box
        onClick={() => setIsExpanded(!isExpanded)}
        sx={(theme) => ({
          alignItems: 'center',
          display: 'flex',
          gap: 1,
          px: 2,
          py: 1.5,
          cursor: 'pointer',
          '&:hover': { bgcolor: theme.brandV2.colors.treadGray9 },
        })}
      >
        <KeyboardArrowDown
          sx={(theme) => ({
            color: theme.brandV2.colors.treadGray4,
            fontSize: 20,
            transition: 'transform .2s linear',
            ...(isExpanded ? {} : { transform: 'rotate(-90deg)' }),
          })}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <OverflowAwareText
            sx={(theme) => ({
              color: theme.brandV2.colors.treadBlack,
              fontSize: '13px',
              fontWeight: 600,
            })}
          >
            {group.groupName}
          </OverflowAwareText>

          <Box sx={{ display: 'flex', gap: 1, mt: 0.25 }}>
            <Typography
              sx={(theme) => ({
                color: theme.brandV2.colors.treadGray3,
                fontSize: '11px',
              })}
            >
              {totalEvents} {t('live_map.geofence_reports.results_panel.events')}
            </Typography>
            <EventBadge count={group.enterCount} type="enter" />
            <EventBadge count={group.exitCount} type="exit" />
          </Box>
        </Box>
      </Box>

      <Collapse in={isExpanded}>
        <Box sx={(theme) => ({ pb: 1, bgcolor: theme.brandV2.colors.treadGray10 })}>
          {group.triggers.map((trigger) => {
            const triggerJobId = trigger.triggeredLocation.job?.id;

            return (
              <GeofenceTriggerCard
                key={trigger.triggeredLocation.id}
                driverName={
                  trigger.triggeredLocation.driver?.fullName ??
                  t('live_map.geofence_reports.results_panel.unknown_driver')
                }
                equipmentName={
                  trigger.triggeredLocation.equipment?.name ??
                  t('live_map.geofence_reports.results_panel.unknown_equipment')
                }
                isLoading={loadingJobId === triggerJobId}
                jobId={trigger.triggeredLocation.job?.jobId ?? t('common.not_applicable')}
                lat={trigger.triggeredLocation.lat}
                lon={trigger.triggeredLocation.lon}
                occurredAt={dayjs
                  .tz(trigger.triggeredLocation.occurredAt)
                  .format('MMM DD, YYYY hh:mm A')}
                onViewClick={triggerJobId ? () => onViewClick(triggerJobId) : undefined}
                orderId={
                  trigger.triggeredLocation.order?.orderId ?? t('common.not_applicable')
                }
                triggerType={trigger.triggerType}
              />
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
}

interface EventBadgeProps {
  count: number;
  type: 'enter' | 'exit';
}

function EventBadge({ count, type }: EventBadgeProps) {
  return (
    <Typography
      sx={(theme) => ({
        color:
          type === 'enter'
            ? theme.brandV2.colors.treadGreen
            : theme.brandV2.colors.treadOrangeDark,
        fontSize: '11px',
        fontWeight: 500,
      })}
    >
      {count} {t(`live_map.geofence_reports.results_panel.${type}`)}
    </Typography>
  );
}
